import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Paper, Tooltip } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { chunk, orderBy } from 'lodash';
import {
  AlertCircle,
  ChevronDown,
  Columns as IconColumns,
  GripVertical,
  Info as IconInfo
} from 'lucide-react';
import React, {
  createElement,
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult
} from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { useIsVisible } from '../../hooks/useIsVisible';
import { reverseDirection, SortDirection } from '../../hooks/useSort';
import { FlexContainer } from '../../layout/Flex';
import {
  queryParamToList,
  setToQueryParam,
  useComboQueryParam,
  useQueryParam
} from '../../routes';
import { isSameSet } from '../../services/set';
import { LinkExternal } from '../LinkExternal';
import { MultiSelector } from '../MultiSelector';
import { SingleSelector, SingleSelectorChip } from '../SingleSelector';
import { IColumn } from '../Table/VirtualizedSortable';

export type SortValue = string | number | boolean;

export type Group<Summary, Item> = {
  key: string;
  summary: Summary;
  items: Item[];
};

export type ItemSorter<
  Item,
  OtherProps extends object | undefined = undefined
> = {
  key: string;
  items: {
    sort:
      | ((s: Item, o: OtherProps) => SortValue)
      | ((s: Item, o: OtherProps) => SortValue)[];
    dir: SortDirection;
  };
};

export type SummarySorter<Summary, Item> = {
  key: string;
  groups: {
    sort: (g: Group<Summary, Item>) => SortValue;
    dir: SortDirection;
  };
};

export type Sorter<
  Summary,
  Item,
  OtherProps extends object | undefined = undefined
> = ItemSorter<Item, OtherProps> &
  SummarySorter<Summary, Item> & { label: string };

export type Grouper<
  Type extends string,
  Summary,
  Item,
  Other extends object | undefined = undefined
> = {
  key: Type;
  label: React.ReactNode;
  toKey: (t: Item) => string;
  toLabel: (g: Group<Summary, Item>) => React.ReactNode;
  defaultSorter: Sorter<Summary, Item, Other>;
};

export type Groupers<
  Type extends string,
  Summary,
  Item,
  Other extends object | undefined = undefined
> = {
  [K in Type]: Grouper<Type, Summary, Item, Other>;
};

export type Sorters<
  Summary,
  Item,
  OtherProps extends object | undefined = undefined
> = { [key: string]: Sorter<Summary, Item, OtherProps> };
export type ItemSorters<
  Item,
  OtherProps extends object | undefined = undefined
> = { [key: string]: ItemSorter<Item, OtherProps> };

export const applySorterOnItems = <
  T extends object,
  O extends object | undefined
>(
  sorter: ItemSorter<T, O>,
  items: T[],
  direction: SortDirection | undefined,
  otherProps: O
) => {
  const { sort, dir } = sorter.items;
  const fns = Array.isArray(sort) ? sort : [sort];
  return orderBy(
    items,
    fns.map((fn) => (item) => fn(item, otherProps)),
    direction || dir
  );
};

export const applySorterOnGroups = <S extends object, T extends object>(
  sorter: SummarySorter<S, T>,
  groups: Group<S, T>[],
  direction: SortDirection | undefined
) => {
  return orderBy(
    groups,
    (x) => sorter.groups.sort(x),
    direction || sorter.groups.dir
  );
};

export const UNKNOWN = 'Unknown';

const MIN_COLUMN_WIDTH = 200;
const STICKY_COLUMN_BORDER_COLOR = '#ebedef';
const TOP_SCROLLBAR_HEIGHT = 6;

const TableHead = styled('div')<{
  sticky?: boolean;
  offset?: number;
  sidePadding?: boolean;
  noTopPadding?: boolean;
}>`
  position: ${(p) => (p.sticky ? 'sticky' : 'relative')};
  top: ${(p) => p.offset || 0}px;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: ${(p) => p.theme.spacing(1)}px
    ${(p) => (p.sidePadding ? p.theme.spacing(0) : p.theme.spacing(0.5))}px;
  padding-top: ${(p) => (p.noTopPadding ? 0 : p.theme.spacing(2))}px;
  font-size: ${(p) => p.theme.custom.fontSize.m}px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  min-height: ${(p) => p.theme.spacing(3)}px;
  background-color: ${(p) => p.theme.palette.background.paper};
  z-index: ${(p) => (p.sticky ? 1 : undefined)};
`;

const HeadCell = styled('div')`
  display: flex;
  align-items: center;
`;

const StickyColumnBorderLine = styled('span')`
  position: absolute;
  right: -1px;
  top: -${(p) => p.theme.spacing(2)}px;
  bottom: -${(p) => p.theme.spacing(2) + 1}px;
  width: 1px;
  background-color: ${STICKY_COLUMN_BORDER_COLOR};
  pointer-events: none;
  z-index: 1;
`;

const DragHandle = styled('span')`
  display: inline-flex;
  align-items: center;
  margin-right: ${(p) => p.theme.spacing(0.5)}px;
  color: ${(p) => p.theme.custom.colors.gray.w400};
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const SortingIndicator = styled('span')`
  font-size: 10px;
  line-height: 1;
  color: ${(p) => p.theme.custom.colors.gray.w400};
  margin-top: 1px;
  margin-right: ${(p) => p.theme.spacing(0.25)}px;
`;

const ExpansionIndicator = styled('span')`
  display: inline-block;
  width: 14px;
  font-size: 11px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  padding-left: ${(p) => p.theme.spacing(0.5)}px;
`;

const TableRow = styled('div')<{
  rowHeight: number;
  cursor: string;
  horizontalScroll?: boolean;
  fitContent?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(p) =>
    p.horizontalScroll || p.fitContent ? 'flex-start' : 'space-between'};
  width: ${(p) =>
    p.horizontalScroll || p.fitContent ? 'max-content' : undefined};
  min-width: ${(p) => (p.horizontalScroll ? '100%' : undefined)};
  cursor: ${(p) => p.cursor};
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(0.5)}px;
  font-size: ${(p) => p.theme.custom.fontSize.m}px;
  min-height: ${(p) => p.rowHeight}px;
  font-variant-numeric: tabular-nums;
  -moz-font-feature-settings: 'tnum';
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
  background-color: ${(p) => p.theme.palette.background.paper};

  :hover {
    background-color: ${(p) => p.theme.custom.colors.gray.w100};
  }
`;

const CellS = styled('div')<{
  showOverflowOnHover: boolean;
  noWrap?: boolean;
  align: 'left' | 'right';
  sidePadding?: boolean;
  height?: boolean;
  highlighted?: boolean;
}>`
  padding: ${(p) => p.theme.spacing(1)}px 0;
  overflow: hidden;
  white-space: ${(p) => (p.noWrap ? 'nowrap' : 'pre-wrap')};
  background-color: inherit;
  position: relative;
  height: ${(p) => (p.height ? 'fit-content' : p.theme.spacing(5))}px;
  align-items: center;
  display: flex;
  min-height: ${(p) => p.theme.spacing(5)}px;

  > div {
    padding: 0
      ${(p) => (p.sidePadding ? p.theme.spacing(0.5) : p.theme.spacing(1))}px;
    line-height: ${(p) => p.theme.spacing(3)}px;
    background-color: inherit;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    text-overflow: ${(p) => (p.noWrap ? 'ellipsis' : 'clip')};
    overflow: hidden;
    justify-content: ${(p) =>
      p.align === 'right' ? 'flex-end' : 'flex-start'};
  }
  &:hover {
    overflow: ${(p) => (p.showOverflowOnHover ? 'visible' : 'hidden')};
    > div {
      position: ${(p) => (p.showOverflowOnHover ? 'absolute' : 'relative')};
      z-index: ${(p) => (p.showOverflowOnHover ? 2 : 1)};
      right: ${(p) => (p.align === 'right' ? '0' : 'unset')};
    }
  }

  & + & {
    padding-left: ${(p) => p.theme.spacing(1)}px;
  }
`;

const HeadWithInfo = styled('span')`
  svg {
    vertical-align: text-top;
  }
`;

const HighlightPill = styled('div')<{ isBlank?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(0.5)}px;
  background-color: #fefaec;
  border-radius: 50px;
  padding: ${(p) => p.theme.spacing(0.5)}px ${(p) => p.theme.spacing(1)}px;
  font-style: ${(p) => (p.isBlank ? 'italic' : 'normal')};
  color: ${(p) => (p.isBlank ? p.theme.custom.colors.gray.w500 : 'inherit')};
  max-width: 100%;
  overflow: hidden;
`;

const Head = <OtherProps extends object | undefined>({
  column,
  otherProps,
  sorted,
  sortable,
  onClick,
  sidePadding,
  dragHandleProps,
  isInDraggableWrapper,
  horizontalScroll,
  fitContent,
  isSticky
}: {
  column: IColumn<any, any, OtherProps>;
  otherProps: OtherProps;
  sorted?: SortDirection | null | undefined;
  sortable?: boolean;
  onClick?: () => void;
  sidePadding?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isInDraggableWrapper?: boolean;
  horizontalScroll?: boolean;
  fitContent?: boolean;
  isSticky?: boolean;
}) => {
  const style = useMemo<CSSProperties>(() => {
    const lockedWidth = Math.max(
      typeof column.width === 'number' ? column.width : 0,
      MIN_COLUMN_WIDTH
    );
    return {
      flexGrow:
        (horizontalScroll || fitContent) && !isInDraggableWrapper
          ? 0
          : isInDraggableWrapper
          ? undefined
          : column.flexGrow,
      width: isInDraggableWrapper
        ? '100%'
        : horizontalScroll
        ? lockedWidth
        : column.width,
      textAlign: column.align,
      fontWeight: (sorted ? 'bolder' : 'normal') as CSSProperties['fontWeight'],
      cursor: sortable ? 'pointer' : 'default',
      ...(horizontalScroll && !isInDraggableWrapper
        ? {
            minWidth: lockedWidth,
            maxWidth: lockedWidth,
            flexShrink: 0
          }
        : {}),
      ...(fitContent && !isInDraggableWrapper && !horizontalScroll
        ? {
            minWidth: column.width,
            maxWidth: column.width,
            flexShrink: 0
          }
        : {}),
      ...(isSticky && !isInDraggableWrapper
        ? {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: 'white',
            overflow: 'visible'
          }
        : {})
    };
  }, [
    column,
    sorted,
    sortable,
    isInDraggableWrapper,
    horizontalScroll,
    fitContent,
    isSticky
  ]);
  const leader = sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '';
  const body = (
    <HeadCell>
      {dragHandleProps && (
        <DragHandle
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          aria-label="Reorder column"
        >
          <GripVertical size={12} />
        </DragHandle>
      )}
      {(sorted === 'asc' || sorted === 'desc') && (
        <SortingIndicator>{leader}</SortingIndicator>
      )}
      {column.head(otherProps)}
    </HeadCell>
  );
  const headInfo = column.headInfo ? column.headInfo(otherProps) : null;
  return (
    <CellS
      showOverflowOnHover={column.showOverflowOnHover || true}
      noWrap={column.noWrap}
      align={column.align || 'left'}
      style={style}
      role={onClick && 'button'}
      onClick={onClick}
      sidePadding={sidePadding}
    >
      {isSticky && !isInDraggableWrapper && <StickyColumnBorderLine />}
      {headInfo ? (
        <Tooltip title={headInfo} aria-label={headInfo} placement="top">
          <HeadWithInfo>
            {body} <IconInfo size={12} />
          </HeadWithInfo>
        </Tooltip>
      ) : (
        body
      )}
    </CellS>
  );
};

const Cell = <T extends object, OtherProps extends object | undefined>({
  d,
  column,
  showOverflowOnHover = true,
  noWrap = false,
  otherProps,
  sidePadding,
  cellProps,
  horizontalScroll,
  fitContent,
  isSticky
}: {
  d: T;
  column: IColumn<T, any, OtherProps>;
  showOverflowOnHover?: boolean;
  noWrap?: boolean;
  otherProps: OtherProps;
  sidePadding?: boolean;
  cellProps?: { adjustHeight?: boolean };
  horizontalScroll?: boolean;
  fitContent?: boolean;
  isSticky?: boolean;
}) => {
  const style = useMemo<CSSProperties>(() => {
    const lockedWidth = Math.max(
      typeof column.width === 'number' ? column.width : 0,
      MIN_COLUMN_WIDTH
    );
    return {
      flexGrow: horizontalScroll || fitContent ? 0 : column.flexGrow,
      width: horizontalScroll ? lockedWidth : column.width,
      textAlign: column.align,
      ...(horizontalScroll
        ? {
            minWidth: lockedWidth,
            maxWidth: lockedWidth,
            flexShrink: 0
          }
        : {}),
      ...(fitContent && !horizontalScroll
        ? {
            minWidth: column.width,
            maxWidth: column.width,
            flexShrink: 0
          }
        : {}),
      ...(isSticky
        ? {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            // Explicit white instead of `inherit`. The cell's parent (TableRow)
            // has its background set via styled-components — but if the row is
            // wrapped in an <a> Link, browser hover/active states can override
            // the row bg with `inherit` cascading through Link in unexpected
            // ways, leaving the sticky cell transparent and other cells
            // showing through visually as the user scrolls horizontally.
            backgroundColor: '#ffffff',
            overflow: 'visible'
          }
        : {})
    };
  }, [column, horizontalScroll, fitContent, isSticky]);

  const isHighlighted = useMemo(() => {
    if (
      !otherProps ||
      !('highlightedFields' in otherProps) ||
      !(otherProps as any).highlightedFields
    ) {
      return false;
    }

    const rowId = (d as any).id;
    const highlightedFields = (otherProps as any).highlightedFields as Record<
      string,
      Set<string>
    >;
    const userFailedFields = highlightedFields[rowId];

    return userFailedFields && userFailedFields.has(column.key);
  }, [d, column.key, otherProps]);

  const cellContent = column.cell(d, otherProps);

  const isBlank = useMemo(() => {
    if (cellContent === null || cellContent === undefined) return true;
    if (typeof cellContent === 'string' && cellContent.trim() === '')
      return true;
    if (React.isValidElement(cellContent)) {
      return false;
    }
    return false;
  }, [cellContent]);

  const renderedContent = useMemo(() => {
    if (isHighlighted) {
      const displayContent = isBlank ? 'Blank field' : cellContent;
      return (
        <HighlightPill isBlank={isBlank}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayContent}
          </span>
          <AlertCircle size={14} style={{ color: 'black' }} />
        </HighlightPill>
      );
    }
    return cellContent;
  }, [isHighlighted, isBlank, cellContent]);

  return (
    <CellS
      align={column.align || 'left'}
      showOverflowOnHover={showOverflowOnHover}
      noWrap={noWrap}
      style={style}
      sidePadding={sidePadding}
      height={cellProps?.adjustHeight}
    >
      {isSticky && <StickyColumnBorderLine />}
      <div>{renderedContent}</div>
    </CellS>
  );
};

const RowsContainer = styled('div')<{ horizontalScroll?: boolean }>`
  &:not(:last-of-type) {
    border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  }
  > a {
    display: block;
    /* Horizontal-scroll gridline fix: the inner row (TableRow) is max-content
       width, but a linked row's anchor wrapper defaults to width 100 percent
       (the viewport), so its border-bottom gridline stopped at the old viewport
       edge and columns scrolled into view had no gridline. Stretch the wrapper
       to the content width so the row separator spans every column. Non-linked
       rows render TableRow directly (already max-content), hence only
       scrolled-in linked rows were affected. */
    ${(p) => (p.horizontalScroll ? 'width: max-content; min-width: 100%;' : '')}
  }
  > div,
  > a {
    border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};

    &:last-of-type {
      border-bottom: 0;
    }
  }
`;

const Rows = <
  T extends object,
  C extends string,
  OtherProps extends object | undefined
>({
  rows,
  columns,
  renderPlaceholder,
  rowHeight,
  rowToKey,
  rowToHref,
  rowToHrefExternal,
  rowToClassName,
  onRowClick,
  otherProps,
  expandableElements,
  sidePadding,
  onExpansionClick,
  cellProps,
  horizontalScroll,
  fitContent,
  placeholderWidth
}: {
  rows: T[];
  columns: IColumn<T, C, OtherProps>[];
  renderPlaceholder: boolean;
  rowHeight: number;
  rowToKey: (row: T, i: number) => string;
  rowToHref?: (d: T, o: OtherProps) => string | null;
  rowToHrefExternal?: (d: T, o: OtherProps) => string | null;
  rowToClassName?: (d: T, o: OtherProps) => string;
  onRowClick?: (d: T, o: OtherProps) => void;
  otherProps: OtherProps;
  expandableElements?: Map<string, JSX.Element>;
  sidePadding?: boolean;
  onExpansionClick?: (rowId: string) => Promise<void>;
  cellProps?: { adjustHeight?: boolean };
  horizontalScroll?: boolean;
  fitContent?: boolean;
  // In horizontalScroll (and fitContent) mode, off-screen chunks must keep the
  // same width as real chunks — otherwise the body's content width shrinks when
  // chunks virtualize and the hugged table's width jumps as the user scrolls.
  placeholderWidth?: number;
}) => {
  if (renderPlaceholder) {
    return (
      <div
        style={{
          height: rows.length * rowHeight,
          width:
            (horizontalScroll || fitContent) && placeholderWidth
              ? placeholderWidth
              : undefined
        }}
      />
    );
  }

  return (
    <>
      {rows.map((d, i) => {
        const key = rowToKey(d, i);
        const href = rowToHref && rowToHref(d, otherProps);
        const externalHref =
          rowToHrefExternal && rowToHrefExternal(d, otherProps);
        const body = (
          <TableRowContainer
            key={key}
            d={d}
            columns={columns}
            rowHeight={rowHeight}
            rowToClassName={rowToClassName}
            onRowClick={onRowClick}
            otherProps={otherProps}
            externalHref={externalHref}
            href={href}
            expandableElement={
              expandableElements && expandableElements.get(key)
            }
            sidePadding={sidePadding}
            onExpansionClick={onExpansionClick}
            rowId={key}
            cellProps={cellProps}
            horizontalScroll={horizontalScroll}
            fitContent={fitContent}
          />
        );
        if (href) {
          return (
            <Link key={key} to={href}>
              {body}
            </Link>
          );
        }
        if (externalHref) {
          return (
            <LinkExternal key={key} href={externalHref}>
              {body}
            </LinkExternal>
          );
        }
        return body;
      })}
    </>
  );
};

const TableRowContainer = <
  T extends object,
  C extends string,
  OtherProps extends object | undefined
>({
  d,
  columns,
  rowHeight,
  rowToClassName,
  onRowClick,
  otherProps,
  href,
  externalHref,
  expandableElement,
  sidePadding,
  onExpansionClick,
  rowId,
  cellProps,
  horizontalScroll,
  fitContent
}: {
  d: T;
  columns: IColumn<T, C, OtherProps>[];
  rowHeight: number;
  rowToClassName?: (d: T, o: OtherProps) => string;
  onRowClick?: (d: T, o: OtherProps) => void;
  href: string | null | undefined;
  externalHref: string | null | undefined;
  otherProps: OtherProps;
  expandableElement?: React.ReactNode;
  sidePadding?: boolean;
  onExpansionClick?: (rowId: string) => Promise<void>;
  rowId: string;
  cellProps?: { adjustHeight?: boolean };
  horizontalScroll?: boolean;
  fitContent?: boolean;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <>
      <TableRow
        rowHeight={rowHeight}
        horizontalScroll={horizontalScroll}
        fitContent={fitContent}
        className={rowToClassName && rowToClassName(d, otherProps)}
        cursor={onRowClick || href || externalHref ? 'pointer' : 'default'}
        role={onRowClick || href || externalHref ? 'button' : undefined}
        onClick={onRowClick && (() => onRowClick(d, otherProps))}
      >
        {expandableElement && (
          <ExpansionIndicator
            onClick={() => {
              setIsVisible((prevVal) => !prevVal);
              if (onExpansionClick) onExpansionClick(rowId);
            }}
          >
            {isVisible ? '▼' : '►'}
          </ExpansionIndicator>
        )}
        {columns.map((c, i) => (
          <Cell
            key={c.key}
            showOverflowOnHover={c.showOverflowOnHover}
            noWrap={c.noWrap}
            column={c}
            d={d}
            otherProps={otherProps}
            sidePadding={sidePadding}
            cellProps={cellProps}
            horizontalScroll={horizontalScroll}
            fitContent={fitContent}
            isSticky={horizontalScroll && i === 0}
          />
        ))}
      </TableRow>
      {isVisible && expandableElement && (
        <FlexContainer spacing={5}>{expandableElement}</FlexContainer>
      )}
    </>
  );
};

const RowsWithVisCheck = <
  T extends object,
  C extends string,
  OtherProps extends object | undefined
>({
  rootMargin,
  ...props
}: {
  rows: T[];
  columns: IColumn<T, C, OtherProps>[];
  rowHeight: number;
  rowToKey: (row: T, i: number) => string;
  rowToHref?: (d: T, o: OtherProps) => string | null;
  rowToHrefExternal?: (d: T, o: OtherProps) => string | null;
  rowToClassName?: (d: T, o: OtherProps) => string;
  onRowClick?: (d: T, o: OtherProps) => void;
  otherProps: OtherProps;
  rootMargin: string;
  expandableElements?: Map<string, JSX.Element>;
  sidePadding?: boolean;
  horizontalScroll?: boolean;
  fitContent?: boolean;
  placeholderWidth?: number;
}) => {
  const { isVisible, ref } = useIsVisible({ rootMargin });

  // Virtualize in BOTH modes. horizontalScroll was previously exempted because,
  // at a small rootMargin, fast vertical scroll could flip an *on-screen* chunk
  // to a placeholder for a frame and blank the sticky first column. The fix is a
  // generous vertical rootMargin (see callers, e.g. UserTable) so on-screen and
  // near-screen chunks stay well inside the "visible" band and never placeholder;
  // only far-off-screen chunks collapse. Without this, a 17k-row org rendered
  // every row into the DOM (~1.16 GB heap) and the page froze.
  const shouldPlaceholder = !isVisible;

  return (
    <RowsContainer ref={ref as any} horizontalScroll={props.horizontalScroll}>
      <Rows {...props} renderPlaceholder={shouldPlaceholder} />
    </RowsContainer>
  );
};

const TransparentHeadContainer = styled('div')((p) => ({
  width: '100%'
}));

const Container = styled(Paper)((p) => ({
  '&&': {
    padding: 0
  }
}));

const HorizontalScrollBody = styled('div')`
  overflow-x: auto;
  /* Contain the body's internal stacking (notably the sticky first column at
     z-index 2) so it can't paint over the sticky header/top-scrollbar or the
     page title as rows scroll up. Without this the first column shares the
     header's z-index but is later in the DOM, so it rendered on top. */
  isolation: isolate;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StickyHeaderContainer = styled('div')<{ offset: number }>`
  position: sticky;
  top: ${(p) => p.offset}px;
  z-index: 2;
  overflow-x: auto;
  overflow-y: hidden;
  background-color: ${(p) => p.theme.palette.background.paper};
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StickyHeaderInner = styled('div')`
  display: flex;
  width: max-content;
`;

const TopScrollbar = styled('div')<{ offset: number }>`
  position: sticky;
  top: ${(p) => p.offset}px;
  z-index: 3;
  overflow-x: auto;
  overflow-y: hidden;
  height: ${TOP_SCROLLBAR_HEIGHT}px;
  background-color: ${(p) => p.theme.palette.background.paper};
  &::-webkit-scrollbar {
    height: ${TOP_SCROLLBAR_HEIGHT}px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.custom.colors.gray.w300};
    border-radius: 3px;
    &:hover {
      background-color: ${(p) => p.theme.custom.colors.gray.w400};
    }
  }
`;

const TopScrollbarSpacer = styled('div')<{ width: number }>`
  width: ${(p) => p.width}px;
  height: 1px;
`;

// TODO check why this doesn't fit on the x axis
export const RowsRendererScrollContainer = styled('div')<{ height: number }>`
  max-height: ${(p) => p.height}px;
  overflow-y: auto;
  overflow-x: hidden;
  background: inherit;
  &::-webkit-scrollbar {
    background-color: ${(p) => p.theme.custom.colors.gray.white};
    width: ${(p) => p.theme.spacing()}px;
    height: ${(p) => p.theme.spacing()}px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background-color: ${(p) => p.theme.custom.colors.gray.w300};
      border-radius: ${(p) => p.theme.custom.border.radius()}px;
      &:hover {
        background-color: ${(p) => p.theme.custom.colors.gray.w400};
      }
    }
  }
`;

export type RowsRendererHeadProps = {
  sticky?: boolean;
  offset?: number;
  transparent?: boolean;
  noTopPadding?: boolean;
};

export const RowsRenderer = <
  T extends object,
  C extends string,
  OtherProps extends object | undefined
>({
  rows,
  columns,
  sorter,
  sortDirection,
  renderHead = false,
  headProps = {},
  chunkSize = 30,
  rootMargin = '400px',
  otherProps,
  rowHeight = 40,
  rowToKey,
  variant = 'contained',
  onHeadClick,
  rowToHref,
  rowToHrefExternal,
  rowToClassName,
  onRowClick,
  expandableElements,
  sidePadding,
  onExpansionClick,
  cellProps = {},
  fullWidth = false,
  onColumnReorder,
  horizontalScroll = false,
  fitContent = false
}: {
  rows: T[];
  columns: IColumn<T, C, OtherProps>[];
  sorter: ItemSorter<T, OtherProps>;
  sortDirection?: SortDirection | undefined;
  renderHead?: boolean;
  headProps?: RowsRendererHeadProps;
  chunkSize?: number;
  rootMargin?: string;
  otherProps: OtherProps;
  rowHeight?: number;
  rowToKey: (row: T, i: number) => string;
  variant?: 'plain' | 'contained';
  onHeadClick?: (
    column: IColumn<T, C, OtherProps>,
    direction: SortDirection | undefined
  ) => void;
  rowToHref?: (d: T, o: OtherProps) => string | null;
  rowToHrefExternal?: (d: T, o: OtherProps) => string | null;
  rowToClassName?: (d: T, o: OtherProps) => string;
  onRowClick?: (d: T, o: OtherProps) => void;
  expandableElements?: Map<string, JSX.Element>;
  sidePadding?: boolean;
  onExpansionClick?: (rowId: string) => Promise<void>;
  cellProps?: { adjustHeight?: boolean };
  fullWidth?: boolean;
  onColumnReorder?: (nextColumns: IColumn<T, C, OtherProps>[]) => void;
  horizontalScroll?: boolean;
  // Opt-in: size the table to the sum of its column widths and pin each column
  // to its defined width (no flexGrow), so the table "hugs" its columns and
  // left-aligns instead of stretching to fill the container. Ignored when
  // horizontalScroll is set. Long values truncate at the column width.
  fitContent?: boolean;
}) => {
  // Memoize the sort: applySorterOnItems runs over the FULL row set (17k+ for large
  // orgs), and without this it re-sorts on every render — including every column
  // add/remove and filter toggle, which don't change the sort at all. That redundant
  // O(n log n) per interaction is a major freeze contributor on big orgs. Deps are
  // referentially stable across column changes (rows/otherProps are memoized upstream),
  // so toggling a column now skips the sort entirely.
  const sorted = useMemo(
    () => applySorterOnItems(sorter, rows, sortDirection, otherProps),
    [sorter, rows, sortDirection, otherProps]
  );
  // In horizontalScroll mode the table container itself is the same width as
  // the viewport — only its rows overflow horizontally. As the user scrolls
  // the inner body, the container's bounding rect translates beyond the
  // viewport (with rootMargin) and IntersectionObserver flips isVisible to
  // false even though the table is still in view vertically. That collapses
  // chunks to placeholders and unmounts row data mid-scroll. Override the
  // horizontal margins to a huge value so only vertical visibility matters.
  // Must be memoized — useIsVisible passes rootMargin into a useEffect dep
  // array, so a new string identity each render would disconnect/reconnect
  // the IntersectionObserver and briefly flicker isVisible to false.
  // horizontalScroll: huge HORIZONTAL margin so horizontal body translation never
  // affects visibility (only vertical position matters), plus a generous VERTICAL
  // buffer (1500px, was 300px) so on-screen and near-screen chunks always stay
  // mounted. That buffer is what makes the re-enabled virtualization safe: chunks
  // only collapse to placeholders when far off-screen, so the sticky first column
  // never blanks for a row the user can actually see.
  const effectiveRootMargin = useMemo(
    () => (horizontalScroll ? `1500px 99999px` : rootMargin),
    [horizontalScroll, rootMargin]
  );
  const { isVisible, ref } = useIsVisible({ rootMargin: effectiveRootMargin });
  const [first = [], ...others] = chunk(sorted, chunkSize); // = [] so that destructuring over an empty array works
  // In fitContent mode the table hugs the sum of its column widths. Precompute it
  // so virtualized (placeholder) chunks keep the same width and the table doesn't
  // jump narrower as off-screen chunks collapse.
  const fitContentWidth = useMemo(
    () =>
      fitContent
        ? columns.reduce(
            (sum, c) => sum + (typeof c.width === 'number' ? c.width : 0),
            0
          )
        : 0,
    [fitContent, columns]
  );
  const additionalStyles = useMemo(
    () => ({
      background: variant === 'plain' ? 'inherit' : undefined,
      width: fitContent ? 'max-content' : fullWidth ? '100%' : undefined
    }),
    [variant, fullWidth, fitContent]
  );

  const headerInnerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const syncLock = useRef(false);
  const [contentWidth, setContentWidth] = useState(0);

  useEffect(() => {
    if (!horizontalScroll || !headerInnerRef.current) return;
    const el = headerInnerRef.current;
    const update = () => setContentWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [horizontalScroll, columns.length]);

  const syncScroll = useCallback((source: 'body' | 'header' | 'top', sl: number) => {
    if (syncLock.current) return;
    syncLock.current = true;
    if (source !== 'body' && bodyRef.current && bodyRef.current.scrollLeft !== sl) {
      bodyRef.current.scrollLeft = sl;
    }
    if (source !== 'header' && headerRef.current && headerRef.current.scrollLeft !== sl) {
      headerRef.current.scrollLeft = sl;
    }
    if (source !== 'top' && topScrollbarRef.current && topScrollbarRef.current.scrollLeft !== sl) {
      topScrollbarRef.current.scrollLeft = sl;
    }
    requestAnimationFrame(() => {
      syncLock.current = false;
    });
  }, []);

  const handleBodyScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncScroll('body', e.currentTarget.scrollLeft),
    [syncScroll]
  );
  const handleHeaderScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncScroll('header', e.currentTarget.scrollLeft),
    [syncScroll]
  );
  const handleTopScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncScroll('top', e.currentTarget.scrollLeft),
    [syncScroll]
  );

  const renderHeadCell = (c: IColumn<T, C, OtherProps>) => {
    const isSorted = c.sortable && c.key === sorter.key;
    const dir = sortDirection || sorter.items.dir;
    return {
      isSorted,
      dir,
      onClick: () => {
        if (onHeadClick) {
          const nextDir = isSorted ? reverseDirection(dir) : undefined;
          onHeadClick(c, nextDir);
        }
      }
    };
  };

  const handleColumnDragEnd = (result: DropResult) => {
    if (!onColumnReorder) {
      return;
    }
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    const next = Array.from(columns);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    onColumnReorder(next);
  };

  const head = renderHead && (
    <TableHead
      sticky={horizontalScroll ? false : headProps.sticky}
      offset={horizontalScroll ? 0 : headProps.offset}
      sidePadding={sidePadding}
      noTopPadding={headProps.noTopPadding}
    >
      {/* Don't gate the draggable branch on `isVisible`. When the body's
          IntersectionObserver flips its state (which can happen mid-scroll
          even with our rootMargin overrides), the React tree swaps between
          the draggable wrapper and the bare-Head fallback. That remount
          re-renders the sticky first column on a fresh DOM node — and
          browsers don't re-establish sticky positioning when the element
          mounts while its scroll ancestor is already scrolled, so the
          column ends up scrolling away with the rest. Pick the branch on
          onColumnReorder alone so the sticky DOM element persists across
          visibility flips. */}
      {onColumnReorder ? (
        <DragDropContext onDragEnd={handleColumnDragEnd}>
          <Droppable droppableId="rows-renderer-columns" direction="horizontal">
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                style={{ display: 'flex', flex: 1, alignItems: 'stretch' }}
              >
                {columns.map((c, i) => {
                  const { isSorted, dir, onClick } = renderHeadCell(c);
                  const isStickyCol = horizontalScroll && i === 0;
                  return (
                    <Draggable
                      key={c.key}
                      draggableId={String(c.key)}
                      index={i}
                    >
                      {(dragProvided, snapshot) => {
                        const colWidth =
                          typeof c.width === 'number' ? c.width : undefined;
                        const resolvedMinWidth = horizontalScroll
                          ? Math.max(colWidth || 0, MIN_COLUMN_WIDTH)
                          : undefined;
                        const stickyStyle: CSSProperties =
                          isStickyCol && !snapshot.isDragging
                            ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                backgroundColor: 'white',
                                overflow: 'visible'
                              }
                            : {};
                        return (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              flexGrow: horizontalScroll ? undefined : c.flexGrow,
                              width: c.width,
                              minWidth: resolvedMinWidth,
                              flexShrink: horizontalScroll ? 0 : undefined,
                              display: 'flex',
                              alignItems: 'stretch',
                              backgroundColor: snapshot.isDragging
                                ? 'rgba(0, 0, 0, 0.03)'
                                : undefined,
                              ...stickyStyle
                            }}
                          >
                            <Head
                              column={c}
                              otherProps={otherProps}
                              sorted={isSorted ? dir : null}
                              sortable={c.sortable}
                              onClick={onClick}
                              dragHandleProps={dragProvided.dragHandleProps}
                              isInDraggableWrapper
                              horizontalScroll={horizontalScroll}
                            />
                            {isStickyCol && !snapshot.isDragging && (
                              <StickyColumnBorderLine />
                            )}
                          </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        // Don't gate the header columns on `isVisible`. `isVisible` tracks
        // the body Container's intersection with the viewport, and in
        // horizontalScroll mode the Container is the same width as the
        // visible viewport — so it can flip false at certain horizontal
        // scroll positions. When that happens the header empties, its
        // ResizeObserver reports width 0, the top scrollbar's spacer
        // collapses, and the next scroll event clamps scrollLeft to 0 →
        // user's scroll position snaps back to the start.
        columns.map((c, i) => {
          const { isSorted, dir, onClick } = renderHeadCell(c);
          return (
            <Head
              key={c.key}
              column={c}
              otherProps={otherProps}
              sorted={isSorted ? dir : null}
              sortable={c.sortable}
              onClick={onClick}
              horizontalScroll={horizontalScroll}
              fitContent={fitContent}
              isSticky={horizontalScroll && i === 0}
            />
          );
        })
      )}
    </TableHead>
  );

  const body = createElement(
    variant === 'contained' ? Container : 'div',
    { ref, style: additionalStyles, elevation: 0, square: true },
    <RowsContainer horizontalScroll={horizontalScroll}>
      {headProps.transparent || horizontalScroll ? null : head}
      <Rows
        rows={first}
        columns={columns}
        renderPlaceholder={horizontalScroll ? false : !isVisible}
        rowHeight={rowHeight}
        rowToKey={rowToKey}
        rowToClassName={rowToClassName}
        otherProps={otherProps}
        rowToHref={rowToHref}
        rowToHrefExternal={rowToHrefExternal}
        onRowClick={onRowClick}
        expandableElements={expandableElements}
        sidePadding={sidePadding}
        onExpansionClick={onExpansionClick}
        cellProps={cellProps}
        horizontalScroll={horizontalScroll}
        fitContent={fitContent}
        placeholderWidth={fitContent ? fitContentWidth : contentWidth}
      />
    </RowsContainer>,
    others.map((xs, i) => (
      <RowsWithVisCheck
        key={i}
        rows={xs}
        columns={columns}
        rowHeight={rowHeight}
        rowToKey={rowToKey}
        otherProps={otherProps}
        rootMargin={effectiveRootMargin}
        rowToHref={rowToHref}
        rowToHrefExternal={rowToHrefExternal}
        rowToClassName={rowToClassName}
        onRowClick={onRowClick}
        expandableElements={expandableElements}
        sidePadding={sidePadding}
        horizontalScroll={horizontalScroll}
        fitContent={fitContent}
        placeholderWidth={fitContent ? fitContentWidth : contentWidth}
      />
    ))
  );

  if (headProps.transparent) {
    return (
      <TransparentHeadContainer>
        {head}
        {body}
      </TransparentHeadContainer>
    );
  }

  if (horizontalScroll) {
    const topOffset = headProps.offset || 0;
    return (
      <>
        <TopScrollbar
          ref={topScrollbarRef}
          offset={topOffset}
          onScroll={handleTopScroll}
        >
          <TopScrollbarSpacer width={contentWidth} />
        </TopScrollbar>
        {head && (
          <StickyHeaderContainer
            ref={headerRef}
            offset={topOffset + TOP_SCROLLBAR_HEIGHT}
            onScroll={handleHeaderScroll}
          >
            <StickyHeaderInner ref={headerInnerRef}>{head}</StickyHeaderInner>
          </StickyHeaderContainer>
        )}
        <HorizontalScrollBody ref={bodyRef} onScroll={handleBodyScroll}>
          {body}
        </HorizontalScrollBody>
      </>
    );
  }

  return body;
};

export const ColumnSelector = <Key extends string>({
  value,
  onChange,
  columns,
  short
}: {
  value: Set<Key>;
  onChange: (nextValue: Set<Key>) => void;
  columns: IColumn<any, Key, any>[];
  short?: boolean;
}) => {
  const theme = useTheme();
  const options = useMemo(
    () =>
      columns.map((c) => ({
        label: c.alternateHead ? c.alternateHead() : c.head(),
        value: c.key
      })),
    [columns]
  );
  return (
    <MultiSelector
      value={value}
      onChange={onChange}
      options={options}
      legend="Columns"
    >
      <Chip
        classes={{
          label: css({
            display: 'inline-flex',
            alignItems: 'center'
          })
        }}
        label={
          <>
            {short ? <IconColumns size={18} /> : 'Columns'}
            <ChevronDown
              size={18}
              className={css({ marginLeft: theme.spacing(1) })}
            />
          </>
        }
      />
    </MultiSelector>
  );
};

export const SortSelector = <S extends object, T extends object>({
  value,
  onChange,
  defaultValue,
  sorters
}: {
  value: Sorter<S, T> | null;
  onChange: (nextValue: Sorter<S, T> | null) => void;
  defaultValue: Sorter<S, T>;
  sorters: Sorters<S, T>;
}) => {
  const allSorters = Object.values(sorters);
  const ss =
    allSorters.indexOf(defaultValue) === -1
      ? [defaultValue, ...allSorters]
      : allSorters;
  const activeValue = value || defaultValue;
  return (
    <SingleSelector
      value={value ? value.key : null}
      onChange={(key) =>
        onChange(key === defaultValue.key ? null : sorters[key])
      }
      legend="Sort by"
      options={ss.map((g) => ({
        value: g.key,
        label: g.label
      }))}
    >
      <SingleSelectorChip
        isApplied={!!value}
        label={`Sort by ${activeValue.label}`}
        appliedLabel={`Sort by ${activeValue.label}`}
        onDelete={() => onChange(null)}
      />
    </SingleSelector>
  );
};

export const useSortQueryParam = <T extends { key: string }>(
  paramName: string,
  sorters: { [key: string]: T }
) => {
  // When updating usage of useComboQueryParam, check for other usages of this fn
  // There is some code which needed to access this param without using the useSortQueryParam
  // directly!
  return useComboQueryParam<[T | null, SortDirection | undefined]>(
    paramName,
    ([pSort, pDir]) => [
      pSort ? sorters[pSort] || null : null,
      pDir ? (pDir as SortDirection) : undefined
    ],
    (xs) =>
      xs
        ? [xs[0] ? xs[0].key : undefined, xs[1] || undefined]
        : [undefined, undefined]
  );
};

export const useColumnsQueryParam = <ColumnName extends string>(
  param: string,
  defaultColumns: ColumnName[]
) => {
  return useQueryParam(
    param,
    (p) => new Set(p ? queryParamToList<ColumnName>(p) : defaultColumns),
    (cs) =>
      cs
        ? isSameSet(cs, new Set(defaultColumns))
          ? undefined
          : setToQueryParam(cs)
        : undefined
  );
};

export const ROW_HEIGHTS = {
  compact: 20,
  dense: 40,
  airy: 80
};

export const ID_TO_ROW_KEY = <T extends { id: string }>(d: T) => d.id;
